import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Container,
  Paper,
  Link as MuiLink,
  Stack,
  CircularProgress,
  Divider
} from '@mui/material';
import moment from 'moment';
import { useRetrieveCouponPublicLazyQuery } from '../../../generated';
import { images } from '../../../../../shared/lib/assets';
import { LINKS, APP_STORE_LINK, PLAY_STORE_LINK, PENDING_PROMO_CODE } from '../../constants';
import { promoButtonStyle } from '../../../../../shared/lib/theme/styleConstants';
import {
  COMPANY_NAME_RIGHTS,
  DATE_FORMAT,
  FILTER_STATUS_ACTIVE,
  LABEL_DESCRIPTION,
  LABEL_SECURE_PONELESS_SERVICES,
  LABEL_STATUS,
  SALES_COUPON_ALT_APP_STORE,
  SALES_COUPON_ALT_GOOGLE_PLAY,
  SALES_COUPON_BUTTON_USE_PROMO_PREFIX,
  SALES_COUPON_CTA_SIGNUP,
  SALES_COUPON_GET_APP_BODY,
  SALES_COUPON_GET_APP_TITLE,
  SALES_COUPON_LABEL_EXPIRATION_DATE,
  SALES_COUPON_PAGE_HEADING,
  SALES_COUPON_STATUS_INACTIVE,
  SALES_COUPON_USE_PROMO_BODY,
  SALES_COUPON_USE_PROMO_TITLE
} from '../../../../../shared/lib/constants';
import { withTrademark } from '../../../../../shared/lib/common/Tokenizer';

const SalesCouponPage = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [retrieveCouponPublic, { data, loading }] = useRetrieveCouponPublicLazyQuery({
    onError: (error) => {
      const graphQLError = error.graphQLErrors?.[0];
      if (graphQLError?.message) {
        setErrorMessage(graphQLError.message);
      }
    }
  });

  useEffect(() => {
    if (code) {
      retrieveCouponPublic({ variables: { retrieveCouponInput: code } });
    }
  }, [code, retrieveCouponPublic]);

  const couponData = data?.retrieveCouponPublic;
  const trimmedTitle = couponData?.title?.trim();
  const pageHeading = trimmedTitle || SALES_COUPON_PAGE_HEADING;
  const trimmedDescription = couponData?.description?.trim();
  const expirationRaw = couponData?.expirationDate?.trim();
  const expirationMoment = expirationRaw ? moment(expirationRaw) : null;
  const hasExpiration = Boolean(expirationRaw) && expirationMoment?.isValid();
  const expirationFormatted = hasExpiration ? expirationMoment!.format(DATE_FORMAT) : null;

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress size={32} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
      <Box
        onClick={() => {
          if (code && couponData?.isActive) {
            localStorage.setItem(PENDING_PROMO_CODE, JSON.stringify({ code, savedAt: Date.now() }));
          }
          navigate(LINKS.REGISTER_LINK);
        }}
        sx={{
          cursor: 'pointer',
          mb: 4,
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)'
        }}>
        <Box
          component="img"
          src="https://855whodoicall.com/images/who_do_i_call_app/banner.png"
          alt="Who Do I Call promotional banner"
          sx={{
            display: 'block',
            width: '100%',
            maxHeight: { xs: 170, md: 170 },
            objectFit: 'cover'
          }}
        />
      </Box>

      <Paper
        elevation={4}
        sx={{
          p: { xs: 3, md: 5 },
          borderRadius: 5,
          background: 'linear-gradient(180deg, #F9FAFB 0%, #FFFFFF 100%)'
        }}
      >
        <Typography 
          variant="h3" 
          fontWeight={800} 
          align="center" 
          gutterBottom 
          sx={{ fontSize: { xs: '2.2rem', md: '3rem' } }}
        >
          {pageHeading}
        </Typography>

        <Typography variant="subtitle2" color="text.secondary" align="center" mb={4}>
          {LABEL_SECURE_PONELESS_SERVICES} <strong>{withTrademark(COMPANY_NAME_RIGHTS)}</strong>
        </Typography>

        {(couponData || errorMessage) && (
          <Box sx={{ ...promoButtonStyle, mb: 4, textAlign: 'center', ...(errorMessage && { backgroundColor: '#FEE2E2', borderColor: '#FECACA' }) }}>
            {errorMessage ? (
              <>
                <Typography variant="h5" fontWeight={700} color="error" gutterBottom>
                  Invalid Coupon
                </Typography>
                {code && (
                  <Typography variant="body1" fontWeight={700} color="error" sx={{ opacity: 0.75 }}>
                    {code}
                  </Typography>
                )}
                <Stack spacing={0.5} sx={{ mt: 1, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    This coupon is no longer valid. If you feel this is an error, please contact
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <MuiLink href="mailto:info@855WhoDoICall.com" underline="hover">
                      info@855WhoDoICall.com
                    </MuiLink>
                  </Typography>
                </Stack>
              </>
            ) : (
              <>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  {SALES_COUPON_USE_PROMO_TITLE}
                </Typography>
                <Typography variant="body1" mb={2} color="text.secondary">
                  {withTrademark(SALES_COUPON_USE_PROMO_BODY)}
                </Typography>

                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    if (code && couponData?.isActive) {
                      localStorage.setItem(PENDING_PROMO_CODE, JSON.stringify({ code, savedAt: Date.now() }));
                    }
                    navigate(LINKS.REGISTER_LINK);
                  }}
                  sx={{ px: 4, py: 1.4, fontWeight: 700, textTransform: 'none', mb: 3 }}
                >
                  {SALES_COUPON_BUTTON_USE_PROMO_PREFIX} {code || "WELCOME100"}
                </Button>

                <Divider sx={{ my: 1, opacity: 0.5 }} />

                <Stack spacing={2} sx={{ mt: 2 }} textAlign="left">

                  {/* Top Row: Expiry and Status side-by-side */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={1}
                  >
                    {hasExpiration && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                          {SALES_COUPON_LABEL_EXPIRATION_DATE}
                        </Typography>
                        <Typography variant="body1" fontWeight={700} color="text.primary">
                          {expirationFormatted}
                        </Typography>
                      </Box>
                    )}

                    {couponData!.isActive != null && (
                      <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                          {LABEL_STATUS}
                        </Typography>
                        <Typography
                          variant="body1"
                          fontWeight={700}
                          sx={{
                            color: couponData!.isActive ? 'success.main' : 'text.disabled'
                          }}
                        >
                          {couponData!.isActive ? FILTER_STATUS_ACTIVE : SALES_COUPON_STATUS_INACTIVE}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Bottom Section: Description */}
                  {trimmedDescription && (
                    <Box sx={{ pt: 1 }}>
                      <Typography variant="body2" color="text.secondary" fontWeight={500} gutterBottom>
                        {LABEL_DESCRIPTION}
                      </Typography>
                      <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.6 }}>
                        {trimmedDescription}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </>
            )}
          </Box>
        )}

        <Box sx={{ ...promoButtonStyle, textAlign: 'center' }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            {SALES_COUPON_GET_APP_TITLE}
          </Typography>
          <Typography variant="body1" mb={2} color="text.secondary">
            {SALES_COUPON_GET_APP_BODY}
          </Typography>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            justifyContent="center" 
            alignItems="center" 
            mt={2}
          >
            <Box component="a" href={PLAY_STORE_LINK} target="_blank" rel="noopener noreferrer">
              <Box component="img" src={images.GOOGLE_PLAY_SVG as any} alt={SALES_COUPON_ALT_GOOGLE_PLAY} sx={{ height: 48, width: 'auto' }} />
            </Box>
            <Box component="a" href={APP_STORE_LINK} target="_blank" rel="noopener noreferrer">
              <Box component="img" src={images.APP_STORE_SVG as any} alt={SALES_COUPON_ALT_APP_STORE} sx={{ height: 48, width: 'auto' }} />
            </Box>
          </Stack>
        </Box>

        <Box textAlign="center" mt={4}>
          <Button
            variant="contained"
            size="large"
            onClick={() => {
              if (code && couponData?.isActive) {
                localStorage.setItem(PENDING_PROMO_CODE, JSON.stringify({ code, savedAt: Date.now() }));
              }
              navigate(LINKS.REGISTER_LINK);
            }}
            sx={{ px: 6, py: 1.6, fontWeight: 700, borderRadius: 3 }}
          >
            {SALES_COUPON_CTA_SIGNUP}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default SalesCouponPage;